import React, { useCallback, useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { apiService } from "../../services";

interface StripeEmbeddedCheckoutProps {
  sku: string;
  userEmail?: string;
  onSuccess?: (sessionId: string, customerEmail: string) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
  /**
   * 24px of white around the Stripe frame. Right inside the pricing modal,
   * where the frame would otherwise touch the modal edge. Wrong on /checkout,
   * which already wraps this in a bordered card — there it was a second frame
   * inside the first, and 48px the payment form could have used.
   */
  padded?: boolean;
}

// Lazy load Stripe only when needed
let stripePromise: Promise<any> | null = null;

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);
  }
  return stripePromise;
};

const StripeEmbeddedCheckout: React.FC<StripeEmbeddedCheckoutProps> = ({
  sku,
  userEmail = "",
  onSuccess,
  onCancel,
  onError,
  padded = true,
}) => {
  const [error, setError] = useState<string | null>(null);

  // Create checkout session and return client secret
  const fetchClientSecret = useCallback(async () => {
    try {
      setError(null);

      const response = await apiService.createCheckoutSession({
        sku,
        customer_email: userEmail,
        ui_mode: "embedded", // Important: use embedded mode
      });

      if (!response.client_secret) {
        throw new Error("No client secret returned from checkout session");
      }

      return response.client_secret;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create checkout session";
      setError(errorMessage);
      if (onError) onError(errorMessage);
      throw err;
    }
  }, [sku, userEmail, onError]);

  const options = { fetchClientSecret };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-medium mb-2">Checkout Error</h3>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Try Again
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="bg-accent text-white px-4 py-2 rounded hover:bg-accent/80"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full bg-white ${padded ? "p-6" : ""}`}>
      <EmbeddedCheckoutProvider stripe={getStripe()} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>

      {/* {onCancel && (
        <div className="mt-4 text-center">
          <button
            onClick={onCancel}
            className="bg-accent text-white px-3 py-1 rounded text-sm hover:bg-accent/80"
          >
            Cancel
          </button>
        </div>
      )} */}
    </div>
  );
};

export default StripeEmbeddedCheckout;
